import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import NotificationToast from '../components/ui_elements/NotificationToast';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE_TOASTS = 3;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 5000) => {
    const id = nextId.current++;
    setToasts(prev => [...prev.slice(-(MAX_VISIBLE_TOASTS - 1)), { id, message, type, duration }]);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className="fixed top-4 right-4 z-[100] flex flex-col items-end gap-3 pointer-events-none"
        aria-live="polite"
      >
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <NotificationToast
              message={t.message}
              type={t.type}
              isVisible
              duration={t.duration}
              onClose={() => dismissToast(t.id)}
              inline
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const noopToast: ToastContextValue = {
  showToast: (message) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('useToast called outside ToastProvider - toast dropped:', message);
    }
  },
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  // Fail soft: toasts are feedback, not critical state - a component rendered
  // outside the provider (isolated tests, storybook) degrades to a no-op.
  return context ?? noopToast;
};
